import { Autocomplete, Box, Chip, CircularProgress, FormControlLabel, FormHelperText, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import { createContext, forwardRef, HTMLAttributes, useContext, useEffect, useMemo, useState } from 'react';
import { CompanySummary, findCompany } from '../../lib/companies';
import { textSearchable } from '../../lib/payPolicyTemplates/api';
import { useCompanyMatches } from '../../lib/useCompanyMatches';
import { CompanyChip, Scope } from './templateDraft';

interface CompanyScopeProps {
  scope: Scope;
  companies: CompanyChip[];
  error?: string;
  onScopeChange: (scope: Scope) => void;
  /** Applied to the companies as they are when it runs, so a lookup finishing late keeps one added meanwhile. */
  onCompaniesChange: (update: (current: CompanyChip[]) => CompanyChip[]) => void;
  /** Shows the scope without letting it change; the company search is left out. */
  disabled?: boolean;
}

// What the open list shows under its last company: a spinner while the next page loads, else how far it got.
const ListEnd = createContext<{ loading: boolean; shown: number; total: number | null }>({ loading: false, shown: 0, total: null });

// A component of its own, not one made per render: a new one each render would remount the list and lose its scroll.
const CompanyListbox = forwardRef<HTMLUListElement, HTMLAttributes<HTMLElement>>(({ children, ...props }, ref) => {
  const { loading, shown, total } = useContext(ListEnd);
  return (
    <ul ref={ref} {...props}>
      {children}
      <Box component="li" role="presentation" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, py: 1, color: 'text.secondary', typography: 'caption' }}>
        {loading ? (
          <>
            <CircularProgress size={16} /> Loading more companies
          </>
        ) : total !== null && shown < total ? (
          `${shown} of ${total} - scroll for more`
        ) : (
          total !== null && `All ${total} shown`
        )}
      </Box>
    </ul>
  );
});

const companyLabel = (company: CompanySummary) => `${company.name || '(no name)'} · ${company.uuid}`;

const CompanyScope = ({ scope, companies, error, onScopeChange, onCompaniesChange, disabled = false }: CompanyScopeProps) => {
  const [input, setInput] = useState('');
  const { matches, total, hasMore, loadMore, loading, error: lookupError, settled, byUuid } = useCompanyMatches(input);
  const added = new Set(companies.map((company) => company.uuid));
  const options = matches.filter((company) => !added.has(company.uuid));

  // A stored template names its companies by uuid only; show their names once.
  const unnamedKey = companies
    .filter((company) => company.name === null)
    .map((company) => company.uuid)
    .join(',');
  useEffect(() => {
    if (!unnamedKey) return undefined;
    let cancelled = false;
    Promise.all(unnamedKey.split(',').map((uuid) => findCompany(uuid).catch(() => null))).then((found) => {
      if (cancelled) return;
      const names = new Map(found.filter((company): company is CompanySummary => company !== null).map((company) => [company.uuid, company.name ?? '']));
      onCompaniesChange((current) => current.map((company) => (company.name === null && unnamedKey.split(',').includes(company.uuid) ? { ...company, name: names.get(company.uuid) ?? '(unknown company)' } : company)));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs per set of unnamed uuids, not per render
  }, [unnamedKey]);

  const add = (company: CompanySummary) => {
    onCompaniesChange((current) => (current.some((other) => other.uuid === company.uuid) ? current : [...current, { uuid: company.uuid, name: company.name ?? '' }]));
    setInput('');
  };

  const listEnd = useMemo(() => ({ loading: loading && matches.length > 0, shown: matches.length, total: byUuid ? null : total }), [loading, matches.length, total, byUuid]);
  let helper = 'Paste a company UUID or type at least 3 characters of its name, spaces count; * skips any text, e.g. vik*69';
  if (lookupError) helper = lookupError;
  else if (settled && !loading && byUuid && matches.length === 0) helper = 'No company with this UUID';
  else if (settled && !loading && byUuid && options.length === 0) helper = 'Already added';
  else if (settled && !loading && !byUuid && textSearchable(input) && matches.length === 0) helper = 'No company with this name';
  if (helper.startsWith('Paste') && !byUuid && total !== null && total > 0) helper = hasMore ? `${matches.length} of ${total} companies, scroll the list for more` : `${total} ${total === 1 ? 'company' : 'companies'}`;
  const helperIsError = Boolean(lookupError) || helper.startsWith('No company') || helper === 'Already added';

  return (
    <Box>
      <Typography variant="subtitle2">Available to</Typography>
      <RadioGroup row value={scope} onChange={(event) => onScopeChange(event.target.value as Scope)}>
        <FormControlLabel value="global" control={<Radio size="small" />} label="Every company (global)" disabled={disabled} />
        <FormControlLabel value="companies" control={<Radio size="small" />} label="Only these companies" disabled={disabled} />
      </RadioGroup>
      {scope === 'companies' && (
        <Box sx={{ mt: 1 }}>
          {!disabled && (
            <ListEnd.Provider value={listEnd}>
              <Autocomplete<CompanySummary, false, false, true>
                freeSolo
                size="small"
                sx={{ maxWidth: 560 }}
                options={options}
                filterOptions={(all) => all}
                getOptionLabel={(option) => (typeof option === 'string' ? option : companyLabel(option))}
                isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                inputValue={input}
                onInputChange={(_, value, reason) => {
                  if (reason !== 'reset') setInput(value);
                }}
                value={null}
                ListboxComponent={CompanyListbox}
                ListboxProps={{
                  // The end of the list stops the scroll there instead of handing it to the page.
                  style: { overscrollBehavior: 'contain' },
                  onScroll: (event) => {
                    const list = event.currentTarget;
                    if (list.scrollTop + list.clientHeight >= list.scrollHeight - 40) loadMore();
                  }
                }}
                onChange={(_, value) => {
                  if (value && typeof value !== 'string') add(value);
                  else if (typeof value === 'string' && byUuid && options.length === 1) add(options[0]);
                }}
                open={options.length > 0 && settled}
                loading={loading}
                renderOption={(props, option) => (
                  <li {...props} key={option.uuid}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {option.name || '(no name)'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {option.uuid}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Company UUID or name"
                    error={helperIsError}
                    helperText={helper}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
            </ListEnd.Provider>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            {companies.map((company) => (
              <Chip
                key={company.uuid}
                label={company.name === null ? company.uuid : `${company.name || '(no name)'} · ${company.uuid.slice(0, 8)}`}
                title={company.uuid}
                onDelete={disabled ? undefined : () => onCompaniesChange((current) => current.filter((other) => other.uuid !== company.uuid))}
              />
            ))}
          </Box>
          {error && <FormHelperText error>{error}</FormHelperText>}
        </Box>
      )}
    </Box>
  );
};

export default CompanyScope;
